import { X } from "lucide-react";
import type React from "react";
import { useState, useEffect, useMemo } from "react";
import useApiHook from "../../api/Api";
import Toast from "../../Toast";
import type {
  ReportFormData,
  UpdateReportProps,
} from "../../../utils/interface";

const UpdateReport: React.FC<UpdateReportProps> = ({
  report,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<ReportFormData>({
    total_sent: "",
    total_delivered: "",
    total_pending: "",
    total_failed: "",
    total_absent_from_network: "",
    invalid: "",
    total_delivered_to_network: "",
    report_date: "",
    sms_class: "",
  });

  // Store original data to compare changes
  const [originalData, setOriginalData] = useState<ReportFormData>({
    total_sent: "",
    total_delivered: "",
    total_pending: "",
    total_failed: "",
    total_absent_from_network: "",
    invalid: "",
    total_delivered_to_network: "",
    report_date: "",
    sms_class: "",
  });

  const { isLoading, makeRequest } = useApiHook(
    `/reports/${report?.id}?_method=PATCH`,
    {
      method: "post",
    }
  );

  const getMaxDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split("T")[0];
  };

  useEffect(() => {
    if (report) {
      const initialData = {
        total_sent: report.total_sent?.toString() || "0",
        total_delivered: report.total_delivered?.toString() || "0",
        total_pending: report.total_pending?.toString() || "0",
        total_failed: report.total_failed?.toString() || "0",
        total_absent_from_network:
          report.total_absent_from_network?.toString() || "0",
        invalid: report.invalid?.toString() || "0",
        total_delivered_to_network:
          report.total_delivered_to_network?.toString() || "0",
        report_date: report.report_date
          ? new Date(report.report_date).toISOString().split("T")[0]
          : "",
        sms_class: report.sms_class || "",
      };

      setFormData(initialData);
      setOriginalData(initialData);
    }
  }, [report]);

  // Check if form has been modified
  const hasChanges = useMemo(() => {
    return Object.keys(formData).some(
      (key) =>
        formData[key as keyof ReportFormData] !==
        originalData[key as keyof ReportFormData]
    );
  }, [formData, originalData]);

  const isFormValid = useMemo(() => {
    return (
      Object.values(formData).every((value) => value.trim() !== "") &&
      formData.report_date !== "" &&
      formData.sms_class !== ""
    );
  }, [formData]);

  const isSubmitDisabled = !hasChanges || !isFormValid || isLoading;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev: ReportFormData) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prev: ReportFormData) => ({
      ...prev,
      [name]: value,
    }));
  };

  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });

  const showToast = (message: string, type: "success" | "error") => {
    setToast({
      message,
      type,
      isVisible: true,
    });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    // Create FormData for method spoofing
    const payload = new FormData();

    // Add the _method field for spoofing PATCH
    payload.append("_method", "PATCH");

    // Add all the form fields
    payload.append("total_sent", formData.total_sent || "0");
    payload.append("total_delivered", formData.total_delivered || "0");
    payload.append("total_pending", formData.total_pending || "0");
    payload.append("total_failed", formData.total_failed || "0");
    payload.append(
      "total_absent_from_network",
      formData.total_absent_from_network || "0"
    );
    payload.append("invalid", formData.invalid || "0");
    payload.append(
      "total_delivered_to_network",
      formData.total_delivered_to_network || "0"
    );
    payload.append("report_date", formData.report_date);
    payload.append("sms_class", formData.sms_class);

    try {
      const response = await makeRequest(payload);

      if (response?.status === 200) {
        showToast("Report updated successfully! 🎉", "success");
        onSuccess();
        onClose();
      } else {
        const errorMessage =
          response?.data?.message || "Failed to update report";
        showToast(errorMessage, "error");
      }
    } catch (error: any) {
      showToast(error?.message || "An error occurred while updating", "error");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-screen overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Update Report</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Total Sent */}
            <div>
              <label
                htmlFor="total_sent"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Total Sent
              </label>
              <input
                type="number"
                id="total_sent"
                name="total_sent"
                value={formData.total_sent}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                min="0"
              />
            </div>

            {/* Total Delivered */}
            <div>
              <label
                htmlFor="total_delivered"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Total Delivered
              </label>
              <input
                type="number"
                id="total_delivered"
                name="total_delivered"
                value={formData.total_delivered}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                min="0"
              />
            </div>

            {/* Total Pending */}
            <div>
              <label
                htmlFor="total_pending"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Total Pending
              </label>
              <input
                type="number"
                id="total_pending"
                name="total_pending"
                value={formData.total_pending}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                min="0"
              />
            </div>

            {/* Total Failed */}
            <div>
              <label
                htmlFor="total_failed"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Total Failed
              </label>
              <input
                type="number"
                id="total_failed"
                name="total_failed"
                value={formData.total_failed}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                min="0"
              />
            </div>

            {/* Total Absent from Network */}
            <div>
              <label
                htmlFor="total_absent_from_network"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Total Absent from Network
              </label>
              <input
                type="number"
                id="total_absent_from_network"
                name="total_absent_from_network"
                value={formData.total_absent_from_network}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                min="0"
              />
            </div>

            {/* Invalid */}
            <div>
              <label
                htmlFor="invalid"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Invalid
              </label>
              <input
                type="number"
                id="invalid"
                name="invalid"
                value={formData.invalid}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                min="0"
              />
            </div>

            {/* Total Delivered to Network */}
            <div>
              <label
                htmlFor="total_delivered_to_network"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Total Delivered to Network
              </label>
              <input
                type="number"
                id="total_delivered_to_network"
                name="total_delivered_to_network"
                value={formData.total_delivered_to_network}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                min="0"
              />
            </div>

            {/* Report Date */}
            <div>
              <label
                htmlFor="report_date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Report Date
              </label>
              <input
                type="date"
                id="report_date"
                name="report_date"
                value={formData.report_date}
                onChange={handleInputChange}
                max={getMaxDate()}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* SMS Class */}
            <div>
              <label
                htmlFor="sms_class"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                SMS Class
              </label>
              <select
                id="sms_class"
                name="sms_class"
                value={formData.sms_class}
                onChange={handleSelectChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Select SMS Class</option>
                <option value="transactional">Transactional</option>
                <option value="otp">OTP</option>
                <option value="promotion">Promotion</option>
              </select>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isSubmitDisabled
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              title={
                !hasChanges
                  ? "No changes to save"
                  : !isFormValid
                  ? "Please fill all required fields"
                  : ""
              }
            >
              {isLoading ? "Updating..." : "Update Report"}
            </button>
          </div>

          {/* Helper text */}
          {!hasChanges && (
            <p className="text-sm text-gray-500 text-center mt-2">
              Make changes to enable the update button
            </p>
          )}
        </form>

        {/* Toast Component */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      </div>
    </div>
  );
};

export default UpdateReport;
